import React, { useMemo, useRef, useState } from 'react';
import {
  X, Plus, Trash2, Loader2, Upload, ExternalLink, Phone, Reply,
  AlertCircle, FileText, Image as ImageIcon, Video, Paperclip, Wand2,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  whatsappCloudService, TemplateDraft, TemplateDraftButton, HeaderFormat,
  TemplateCategory, ButtonType, TEMPLATE_LANGUAGES, MEDIA_RULES,
} from '@/services/integrations/whatsappCloudService';
import { WA_BUBBLE_BG } from '@/utils/whatsappTemplate';

const MAX_HEADER = 60;
const MAX_BODY = 1024;
const MAX_FOOTER = 60;
const MAX_BUTTON_TEXT = 25;
const MAX_BUTTONS = 10;

const PLACEHOLDER_RE = /\{\{\s*(\d+)\s*\}\}/g;

/** Distinct variable numbers used in a string, ascending. */
const placeholdersOf = (s: string): number[] => {
  const found = new Set<number>();
  for (const m of (s || '').matchAll(PLACEHOLDER_RE)) found.add(Number(m[1]));
  return [...found].sort((a, b) => a - b);
};

/** Meta only accepts lowercase letters, digits and underscores. */
const slugify = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 512);

/** Keystroke-safe variant: keeps a trailing underscore so "crm_" can be typed.
 *  The full slugify runs on blur and on submit. */
const slugifyTyping = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+/, '').slice(0, 512);

const HEADER_FORMATS: { value: HeaderFormat; label: string; icon: React.ReactNode }[] = [
  { value: 'NONE', label: 'None', icon: <X className="w-3.5 h-3.5" /> },
  { value: 'TEXT', label: 'Text', icon: <FileText className="w-3.5 h-3.5" /> },
  { value: 'IMAGE', label: 'Image', icon: <ImageIcon className="w-3.5 h-3.5" /> },
  { value: 'VIDEO', label: 'Video', icon: <Video className="w-3.5 h-3.5" /> },
  { value: 'DOCUMENT', label: 'Document', icon: <Paperclip className="w-3.5 h-3.5" /> },
];

const BUTTON_TYPES: { value: ButtonType; label: string; icon: React.ReactNode }[] = [
  { value: 'QUICK_REPLY', label: 'Quick reply', icon: <Reply className="w-3.5 h-3.5" /> },
  { value: 'URL', label: 'Visit website', icon: <ExternalLink className="w-3.5 h-3.5" /> },
  { value: 'PHONE_NUMBER', label: 'Call phone', icon: <Phone className="w-3.5 h-3.5" /> },
];

const Counter: React.FC<{ used: number; max: number }> = ({ used, max }) => (
  <span className={`text-[11px] ${used > max ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
    {used}/{max}
  </span>
);

const Field: React.FC<{
  label: string; hint?: string; counter?: React.ReactNode; children: React.ReactNode;
}> = ({ label, hint, counter, children }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-xs font-medium text-gray-700">{label}</label>
      {counter}
    </div>
    {children}
    {hint && <p className="mt-1 text-[11px] text-gray-400">{hint}</p>}
  </div>
);

interface Props {
  onClose: () => void;
  onCreated: () => void;
  /** Existing template names — used to catch a duplicate before Meta rejects it. */
  existingNames: string[];
}

const AddTemplateModal: React.FC<Props> = ({ onClose, onCreated, existingNames }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<TemplateCategory>('MARKETING');
  const [language, setLanguage] = useState('en');

  const [headerFormat, setHeaderFormat] = useState<HeaderFormat>('NONE');
  const [headerText, setHeaderText] = useState('');
  const [headerExample, setHeaderExample] = useState('');
  const [headerHandle, setHeaderHandle] = useState('');
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [headerFilename, setHeaderFilename] = useState('');
  const [uploading, setUploading] = useState(false);

  const [body, setBody] = useState('');
  // Keyed by variable number so editing the body doesn't lose samples already typed.
  const [samples, setSamples] = useState<Record<number, string>>({});
  const [footer, setFooter] = useState('');
  const [buttons, setButtons] = useState<TemplateDraftButton[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const bodyVars = useMemo(() => placeholdersOf(body), [body]);
  const headerVars = useMemo(() => placeholdersOf(headerText), [headerText]);
  const isMediaHeader = headerFormat === 'IMAGE' || headerFormat === 'VIDEO' || headerFormat === 'DOCUMENT';

  // Meta rejects gapped numbering outright, so surface it before submitting.
  const varsAreSequential = bodyVars.every((n, i) => n === i + 1);

  const nameTaken = useMemo(
    () => !!name && existingNames.some((n) => n.toLowerCase() === name.toLowerCase()),
    [name, existingNames],
  );

  // The CRM only ever lists templates named crm_*. Submitting one outside that
  // prefix is allowed — Meta approves it and marketing can send it — but it will
  // never appear in the inbox picker, a journey step or the grid behind this modal,
  // so say so at the point of naming rather than leaving someone hunting for a
  // template they watched get approved.
  const outsideCrmPrefix = !!name && !name.startsWith('crm');

  const urlButtonCount = buttons.filter((b) => b.type === 'URL').length;
  const phoneButtonCount = buttons.filter((b) => b.type === 'PHONE_NUMBER').length;

  /** Insert the next free variable number at the cursor. */
  const insertVariable = () => {
    const next = bodyVars.length ? Math.max(...bodyVars) + 1 : 1;
    const token = `{{${next}}}`;
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const updated = body.slice(0, start) + token + body.slice(end);
    setBody(updated);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  /** Rewrite {{7}}, {{2}} … into {{1}}, {{2}} … keeping each sample with its variable. */
  const renumberVariables = () => {
    const order = bodyVars;
    const remap = new Map(order.map((n, i) => [n, i + 1]));
    setBody(body.replace(PLACEHOLDER_RE, (_, n) => `{{${remap.get(Number(n)) ?? n}}}`));
    setSamples((prev) => {
      const next: Record<number, string> = {};
      order.forEach((n, i) => { next[i + 1] = prev[n] ?? ''; });
      return next;
    });
  };

  const pickMedia = async (file: File | undefined) => {
    if (!file) return;
    const rules = MEDIA_RULES[headerFormat];
    if (rules && file.size > rules.maxMB * 1024 * 1024) {
      toast.error(`${headerFormat} header must be under ${rules.maxMB}MB`);
      return;
    }
    setUploading(true);
    try {
      const res = await whatsappCloudService.uploadHeaderMedia(file, headerFormat);
      setHeaderHandle(res.handle);
      setHeaderMediaUrl(res.media_url);
      setHeaderFilename(res.filename || file.name);
      toast.success('Header media uploaded');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Media upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addButton = (type: ButtonType) => {
    if (buttons.length >= MAX_BUTTONS) return;
    if (type === 'URL' && urlButtonCount >= 2) { toast.info('At most 2 URL buttons'); return; }
    if (type === 'PHONE_NUMBER' && phoneButtonCount >= 1) { toast.info('At most 1 call button'); return; }
    setButtons((b) => [...b, { type, text: '' }]);
  };

  const patchButton = (i: number, patch: Partial<TemplateDraftButton>) =>
    setButtons((prev) => prev.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));

  /** First blocking problem, or null. Mirrors _validate_create on the server. */
  const problem = useMemo((): string | null => {
    if (!name.trim()) return 'Give the template a name';
    if (nameTaken) return 'A template with this name already exists';
    if (headerFormat === 'TEXT') {
      if (!headerText.trim()) return 'Header text is empty';
      if (headerText.length > MAX_HEADER) return 'Header text is too long';
      if (headerVars.length > 1 || (headerVars.length === 1 && headerVars[0] !== 1))
        return 'A text header supports exactly one variable, {{1}}';
      if (headerVars.length === 1 && !headerExample.trim())
        return 'Add a sample value for the header variable';
    }
    if (isMediaHeader && !headerHandle) return 'Upload the header file';
    if (!body.trim()) return 'Body text is empty';
    if (body.length > MAX_BODY) return 'Body text is too long';
    if (!varsAreSequential) return 'Body variables must be numbered consecutively from {{1}}';
    if (bodyVars.some((n) => !(samples[n] || '').trim())) return 'Fill in every variable sample';
    if (footer.length > MAX_FOOTER) return 'Footer is too long';
    for (const [i, b] of buttons.entries()) {
      if (!b.text.trim()) return `Button ${i + 1} needs a label`;
      if (b.text.length > MAX_BUTTON_TEXT) return `Button ${i + 1} label is too long`;
      if (b.type === 'URL') {
        if (!/^https?:\/\//.test(b.url || '')) return `Button ${i + 1} needs a valid URL`;
        if (placeholdersOf(b.url || '').length && !(b.example || '').trim())
          return `Button ${i + 1} needs a sample URL`;
      }
      if (b.type === 'PHONE_NUMBER' && !(b.phone_number || '').trim())
        return `Button ${i + 1} needs a phone number`;
    }
    return null;
  }, [name, nameTaken, headerFormat, headerText, headerVars, headerExample, isMediaHeader,
      headerHandle, body, bodyVars, varsAreSequential, samples, footer, buttons]);

  const submit = async () => {
    if (problem) { toast.error(problem); return; }
    const draft: TemplateDraft = {
      name: slugify(name),
      language,
      category,
      header: headerFormat === 'NONE'
        ? { format: 'NONE' }
        : headerFormat === 'TEXT'
          ? { format: 'TEXT', text: headerText.trim(), example: headerExample.trim() }
          : {
              format: headerFormat,
              handle: headerHandle,
              media_url: headerMediaUrl,
              filename: headerFilename,
            },
      body: { text: body.trim(), examples: bodyVars.map((n) => (samples[n] || '').trim()) },
      footer: footer.trim() || undefined,
      buttons: buttons.map((b) => ({
        type: b.type,
        text: b.text.trim(),
        ...(b.type === 'URL' ? { url: (b.url || '').trim(), example: (b.example || '').trim() || undefined } : {}),
        ...(b.type === 'PHONE_NUMBER' ? { phone_number: (b.phone_number || '').trim() } : {}),
      })),
    };
    setSubmitting(true);
    try {
      const res = await whatsappCloudService.createTemplate(draft);
      toast.success(`Submitted — Meta says ${res.status}`);
      onCreated();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Template submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  /** Swap variables for their samples so the preview reads like a real message. */
  const filled = (s: string, map: Record<number, string>): string =>
    (s || '').replace(PLACEHOLDER_RE, (tok, n) => map[Number(n)]?.trim() || tok);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">New WhatsApp template</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Submitted to Meta for review — it stays PENDING until they approve it, usually within a few minutes.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto grid grid-cols-1 lg:grid-cols-[1fr_320px]">
          {/* ---- Form ---- */}
          <div className="p-6 space-y-5 border-r border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Name" hint="Lowercase, numbers and underscores · start with crm_">
                <input
                  value={name}
                  onChange={(e) => setName(slugifyTyping(e.target.value))}
                  onBlur={() => setName((n) => slugify(n))}
                  placeholder="crm_reorder_reminder"
                  className={`w-full px-3 py-2 text-sm border rounded-lg outline-none focus:border-brandColor ${
                    nameTaken ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
              </Field>
              <Field label="Category">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as TemplateCategory)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor bg-white"
                >
                  <option value="MARKETING">Marketing</option>
                  <option value="UTILITY">Utility</option>
                </select>
              </Field>
              <Field label="Language">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor bg-white"
                >
                  {TEMPLATE_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </Field>
            </div>
            {nameTaken && (
              <p className="-mt-3 text-[11px] text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> A template already uses this name.
              </p>
            )}
            {!nameTaken && outsideCrmPrefix && (
              <p className="-mt-3 text-[11px] text-amber-600 flex items-start gap-1">
                <AlertCircle className="w-3 h-3 mt-px flex-shrink-0" />
                <span>
                  The CRM only shows templates named <code className="font-mono">crm_…</code>.
                  This one will be submitted and approved, but it won’t appear in the
                  inbox, in journeys, or in the list behind this modal.
                </span>
              </p>
            )}

            {/* Header */}
            <div className="border-t border-gray-100 pt-4">
              <label className="text-xs font-medium text-gray-700 block mb-2">Header <span className="text-gray-400 font-normal">· optional</span></label>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {HEADER_FORMATS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => {
                      setHeaderFormat(f.value);
                      setHeaderHandle(''); setHeaderMediaUrl(''); setHeaderFilename('');
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      headerFormat === f.value
                        ? 'border-brandColor text-brandColor bg-green-50'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {f.icon}{f.label}
                  </button>
                ))}
              </div>

              {headerFormat === 'TEXT' && (
                <div className="space-y-2">
                  <Field label="Header text" counter={<Counter used={headerText.length} max={MAX_HEADER} />}>
                    <input
                      value={headerText}
                      onChange={(e) => setHeaderText(e.target.value)}
                      placeholder="Your order is on its way"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                    />
                  </Field>
                  {headerVars.length > 0 && (
                    <Field label="Sample for {{1}}">
                      <input
                        value={headerExample}
                        onChange={(e) => setHeaderExample(e.target.value)}
                        placeholder="ZH425376"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                      />
                    </Field>
                  )}
                </div>
              )}

              {isMediaHeader && (
                <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center">
                  <input
                    ref={fileRef}
                    type="file"
                    accept={MEDIA_RULES[headerFormat]?.accept}
                    onChange={(e) => pickMedia(e.target.files?.[0])}
                    className="hidden"
                  />
                  {headerHandle ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                      <Paperclip className="w-4 h-4 text-brandColor" />
                      <span className="truncate max-w-[240px]">{headerFilename}</span>
                      <button
                        onClick={() => {
                          setHeaderHandle(''); setHeaderMediaUrl(''); setHeaderFilename('');
                        }}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploading ? 'Uploading to Meta…' : `Choose ${headerFormat.toLowerCase()}`}
                    </button>
                  )}
                  <p className="mt-2 text-[11px] text-gray-400">
                    {MEDIA_RULES[headerFormat]?.accept.replace(/[a-z]+\//g, '').toUpperCase()} · up to {MEDIA_RULES[headerFormat]?.maxMB}MB.
                    This file is the approval sample; each send supplies its own media.
                  </p>
                </div>
              )}
            </div>

            {/* Body */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700">Body</label>
                <div className="flex items-center gap-2">
                  <Counter used={body.length} max={MAX_BODY} />
                  <button
                    onClick={insertVariable}
                    className="flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    <Plus className="w-3 h-3" /> Variable
                  </button>
                </div>
              </div>
              <textarea
                ref={bodyRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                placeholder="Hi {{1}}, your Zeroharm order {{2}} is out for delivery today."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor resize-y"
              />

              {!varsAreSequential && (
                <div className="mt-2 flex items-center justify-between gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                  <span className="text-[11px] text-amber-800 flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Variables must run consecutively from {'{{1}}'} — Meta rejects gaps.
                  </span>
                  <button
                    onClick={renumberVariables}
                    className="flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
                  >
                    <Wand2 className="w-3 h-3" /> Renumber
                  </button>
                </div>
              )}

              {bodyVars.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-[11px] text-gray-500">
                    Meta reviews the template using these samples — they aren't sent to customers.
                  </p>
                  {bodyVars.map((n) => (
                    <div key={n} className="flex items-center gap-2">
                      <span className="px-2 py-1 rounded bg-brandColor/10 text-brandColor text-xs font-mono flex-shrink-0">
                        {`{{${n}}}`}
                      </span>
                      <input
                        value={samples[n] || ''}
                        onChange={(e) => setSamples((p) => ({ ...p, [n]: e.target.value }))}
                        placeholder="Sample value"
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 pt-4">
              <Field
                label="Footer"
                hint="No variables allowed here."
                counter={<Counter used={footer.length} max={MAX_FOOTER} />}
              >
                <input
                  value={footer}
                  onChange={(e) => setFooter(e.target.value)}
                  placeholder="Zeroharm Sciences"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                />
              </Field>
            </div>

            {/* Buttons */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-gray-700">
                  Buttons <span className="text-gray-400 font-normal">· optional</span>
                </label>
                <span className="text-[11px] text-gray-400">{buttons.length}/{MAX_BUTTONS}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {BUTTON_TYPES.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => addButton(t.value)}
                    disabled={buttons.length >= MAX_BUTTONS}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <Plus className="w-3 h-3" />{t.icon}{t.label}
                  </button>
                ))}
              </div>

              <div className="space-y-2">
                {buttons.map((b, i) => (
                  <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-medium text-gray-500 flex items-center gap-1 flex-shrink-0">
                        {BUTTON_TYPES.find((t) => t.value === b.type)?.icon}
                        {BUTTON_TYPES.find((t) => t.value === b.type)?.label}
                      </span>
                      <input
                        value={b.text}
                        onChange={(e) => patchButton(i, { text: e.target.value })}
                        placeholder="Button label"
                        maxLength={MAX_BUTTON_TEXT}
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                      />
                      <button
                        onClick={() => setButtons((p) => p.filter((_, idx) => idx !== i))}
                        className="text-gray-400 hover:text-red-500 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {b.type === 'URL' && (
                      <>
                        <input
                          value={b.url || ''}
                          onChange={(e) => patchButton(i, { url: e.target.value })}
                          placeholder="https://zeroharm.in/products/{{1}}"
                          className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                        />
                        {placeholdersOf(b.url || '').length > 0 && (
                          <input
                            value={b.example || ''}
                            onChange={(e) => patchButton(i, { example: e.target.value })}
                            placeholder="Sample full URL — https://zeroharm.in/products/ashwagandha"
                            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                          />
                        )}
                      </>
                    )}
                    {b.type === 'PHONE_NUMBER' && (
                      <input
                        value={b.phone_number || ''}
                        onChange={(e) => patchButton(i, { phone_number: e.target.value })}
                        placeholder="+919876543210"
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brandColor"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ---- Live preview ---- */}
          <div className="p-5 bg-gray-50">
            <div className="text-xs font-medium text-gray-500 mb-2">Preview</div>
            <div className="rounded-xl p-3 sticky top-0" style={{ background: WA_BUBBLE_BG }}>
              <div className="bg-white rounded-lg rounded-tl-none shadow-sm p-3">
                {headerFormat === 'TEXT' && headerText && (
                  <div className="text-sm font-semibold text-gray-900 mb-1">
                    {filled(headerText, { 1: headerExample })}
                  </div>
                )}
                {isMediaHeader && (
                  <div className="mb-2 h-24 rounded bg-gray-100 flex flex-col items-center justify-center text-gray-400 gap-1">
                    {headerFormat === 'IMAGE' && <ImageIcon className="w-6 h-6" />}
                    {headerFormat === 'VIDEO' && <Video className="w-6 h-6" />}
                    {headerFormat === 'DOCUMENT' && <Paperclip className="w-6 h-6" />}
                    <span className="text-[10px] px-2 truncate max-w-full">
                      {headerFilename || `${headerFormat.toLowerCase()} header`}
                    </span>
                  </div>
                )}
                <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed break-words">
                  {filled(body, samples) || (
                    <span className="text-gray-300">Your message will appear here…</span>
                  )}
                </div>
                {footer && <div className="text-[11px] text-gray-400 mt-2">{footer}</div>}
              </div>
              {buttons.length > 0 && (
                <div className="mt-1.5 space-y-1">
                  {buttons.map((b, i) => (
                    <div
                      key={i}
                      className="bg-white rounded-lg shadow-sm py-2 text-center text-sm text-[#00a5f4] font-medium flex items-center justify-center gap-1.5"
                    >
                      {b.type === 'URL' && <ExternalLink className="w-3.5 h-3.5" />}
                      {b.type === 'PHONE_NUMBER' && <Phone className="w-3.5 h-3.5" />}
                      {b.text || 'Button'}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <p className="mt-3 text-[11px] text-gray-400 leading-relaxed">
              Variables are shown filled with your samples. Real sends substitute journey data
              mapped on the WhatsApp step.
            </p>
          </div>
        </div>

        {/* Footer bar */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100">
          <span className="text-[11px] text-gray-400 flex items-center gap-1.5 min-w-0">
            {problem && <><AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" /><span className="truncate">{problem}</span></>}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting || !!problem}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90 disabled:opacity-50"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit for review
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTemplateModal;
